import { CharlieButton } from '@ver.id/libs.ui.charlie';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { type ClientInfo, type RequestType } from '../../types';
import { useTranslation } from '../../i18n/i18n';
import { useAppPresentation } from '../../hooks/useAppPresentation';
import MobileLayout from '../../layouts/MobileLayout';
import FlowFooter from '../../components/FlowFooter';
import LoadingState from '../../components/LoadingState';
import DataTableLayout from '../../components/DataTableLayout';
import DataTable from '../../components/DataTable';

type Props = {
  presentationCompact: string;
  client?: ClientInfo;
  requestType?: RequestType;
  requestedDataGroups?: RequestedDataGroup[];
  onAccept: () => void;
  onCancel: () => void;
};

export default function MobileAppPresentationPage({
  presentationCompact,
  client,
  requestType,
  requestedDataGroups,
  onAccept,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const { result, selectedIndex, setSelectedIndex, isReady } =
    useAppPresentation(presentationCompact, requestedDataGroups);
  const isIssuance = requestType === 'issuance';
  const clientName = client?.name || t('dataReview.unknownClient');

  if (!isReady) {
    return (
      <MobileLayout>
        <LoadingState title={t('dataReview.loadingNames')} />
      </MobileLayout>
    );
  }

  if (!result.ok) {
    return (
      <MobileLayout>
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="flex flex-col items-center gap-4 text-center max-w-lg">
            <p className="text-sm text-destructive">
              {t('dataReview.errorLoadingNames')}
            </p>
            <CharlieButton variant="outline" onClick={onCancel}>
              {t('dataReview.cancel')}
            </CharlieButton>
          </div>
        </div>
      </MobileLayout>
    );
  }

  const { valueMap } = result;

  return (
    <MobileLayout
      footer={
        <FlowFooter
          primaryLabel={
            isIssuance
              ? t('dataReview.acceptReceive')
              : t('dataReview.acceptShare')
          }
          onPrimary={onAccept}
          secondaryLabel={t('dataReview.cancel')}
          onSecondary={onCancel}
        />
      }
    >
      <div className="flex flex-1 flex-col items-center gap-8 w-full">
        <div className="flex flex-col items-center gap-3 text-center max-w-lg">
          <h2 className="text-2xl font-semibold text-foreground">
            {t('dataReview.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isIssuance
              ? t('dataReview.subtitleIssuance', { clientName })
              : t('dataReview.subtitleDisclosure', { clientName })}
          </p>
        </div>
        <div className="w-full max-w-2xl">
          <DataTableLayout
            groups={requestedDataGroups!}
            selectedIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
            issuedByKey="dataReview.issuedBy"
            variant="mobile"
          >
            {(activeGroup) => {
              const credentialValues = valueMap.get(activeGroup.credentialUuid);
              return (
                <DataTable
                  group={activeGroup}
                  columns={[
                    {
                      header: t('dataReview.attribute'),
                      render: (f) => f.name,
                    },
                    {
                      header: t('dataReview.value'),
                      render: (f) =>
                        credentialValues?.get(f.attributeUuid) ?? '-',
                    },
                  ]}
                />
              );
            }}
          </DataTableLayout>
        </div>
      </div>
    </MobileLayout>
  );
}
